// ops-org-cdn-domains-tab.tsx — CDN domain list under org detail (implicit customer token).
// Calls GET /api/v3/cdn/domains via impersonation-client using the org's cached customer token.
// Each row links to /operations/act-as/cdn/:domainId for the full 23-tab console.
import { Globe, ExternalLink } from 'lucide-react';
import { Button, Card, CardBody, CardHeader, CardTitle, Skeleton } from '@vnetwork/ui/primitives';
import { DataTable, EmptyState, StatusPill, type ColumnDef } from '@vnetwork/ui/data';
import { Alert } from '@vnetwork/ui/feedback';
import { useActAsCDNDomains, type ActAsCDNDomainDetail } from '@/api/act-as-cdn';
import { formatDate } from '@/lib/format';
import { TableLink } from '@/components/table-link';

/* ── Status tone helper ──────────────────────────────────────────────────── */

function statusTone(status: string): 'success' | 'warning' | 'danger' | 'muted' | 'neutral' {
  switch (status) {
    case 'active': return 'success';
    case 'deploying':
    case 'pending': return 'warning';
    case 'suspended':
    case 'error': return 'danger';
    case 'terminated': return 'muted';
    default: return 'neutral';
  }
}

function makeColumns(): ColumnDef<ActAsCDNDomainDetail, unknown>[] {
  return [
    {
      id: 'domain',
      accessorKey: 'domain',
      header: 'Domain',
      cell: ({ row }) => {
        const d = row.original;
        return (
          <TableLink to={`/operations/act-as/cdn/${encodeURIComponent(d.id)}`}>
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 'var(--vn-space-1)' }}>
              <Globe size={13} style={{ color: 'var(--vn-fg-muted)', flexShrink: 0 }} />
              {d.domain}
              <ExternalLink size={12} style={{ color: 'var(--vn-fg-subtle)', flexShrink: 0 }} />
            </span>
          </TableLink>
        );
      },
    },
    {
      id: 'status',
      header: 'Status',
      cell: ({ row }) => (
        <StatusPill tone={statusTone(row.original.status ?? '')}>
          {row.original.status ?? '—'}
        </StatusPill>
      ),
    },
    {
      id: 'cname',
      header: 'CNAME',
      cell: ({ row }) => {
        const cname = row.original.cname_target ?? row.original.cname;
        return cname ? (
          <span
            style={{
              fontFamily: 'monospace',
              fontSize: 'var(--vn-font-size-xs)',
              color: 'var(--vn-fg-subtle)',
            }}
          >
            {cname}
          </span>
        ) : (
          <span style={{ color: 'var(--vn-fg-subtle)' }}>—</span>
        );
      },
    },
    {
      id: 'updated_at',
      header: 'Updated',
      cell: ({ row }) =>
        row.original.updated_at ? (
          <span style={{ fontSize: 'var(--vn-font-size-xs)', color: 'var(--vn-fg-subtle)' }}>
            {formatDate(row.original.updated_at)}
          </span>
        ) : (
          <span style={{ color: 'var(--vn-fg-subtle)' }}>—</span>
        ),
    },
  ];
}

/* ── Tab component ───────────────────────────────────────────────────────── */

interface OpsOrgCdnDomainsTabProps {
  /** org_id — only used to check session availability (impersonation-client is pre-set). */
  orgId: string;
}

/**
 * CDN domain list for the org — fetched via the org's cached customer token.
 * Requires that the org's impersonation session was minted on page entry (by
 * OperationsOrgDetailPage). If no session exists an inline notice is shown
 * instead of crashing (e.g. internal orgs without a DC contact).
 */
export function OpsOrgCdnDomainsTab({ orgId: _orgId }: OpsOrgCdnDomainsTabProps) {
  const columns = makeColumns();
  const query = useActAsCDNDomains({}, true);

  if (query.isError) {
    return (
      <Alert
        tone="danger"
        title="Failed to load CDN domains"
        actions={
          <Button size="sm" variant="ghost" onClick={() => query.refetch()}>
            Retry
          </Button>
        }
      >
        The customer token may be unavailable. Check that this org has an impersonatable contact.
      </Alert>
    );
  }

  if (query.isLoading) {
    return (
      <Card>
        <CardHeader><CardTitle>CDN Domains</CardTitle></CardHeader>
        <CardBody>
          <div style={{ display: 'grid', gap: 'var(--vn-space-2)' }}>
            {Array.from({ length: 5 }).map((_, i) => (
              <Skeleton key={i} style={{ height: 24 }} />
            ))}
          </div>
        </CardBody>
      </Card>
    );
  }

  const rows = query.data?.results ?? [];
  const total = query.data?.pagination.total ?? rows.length;

  return (
    <Card>
      <CardHeader
        actions={
          <Button
            size="sm"
            variant="ghost"
            onClick={() => query.refetch()}
            disabled={query.isFetching}
          >
            {query.isFetching ? 'Refreshing…' : 'Refresh'}
          </Button>
        }
      >
        <CardTitle>CDN Domains {total > 0 ? `(${total})` : ''}</CardTitle>
      </CardHeader>
      <CardBody style={{ padding: 0 }}>
        <DataTable
          columns={columns}
          data={rows}
          getRowId={(r) => r.id}
          dense
          emptyState={
            <EmptyState
              icon={<Globe size={24} />}
              title="No CDN domains"
              description="No CDN domains found for this organization."
            />
          }
          footer={rows.length > 0 ? `${total} domain${total !== 1 ? 's' : ''}` : undefined}
        />
      </CardBody>
    </Card>
  );
}
